import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "./landing/Header";
import { userAPI } from "../services/api"; // Import the API service

// Skeleton Loader Component for Doctor Cards
const DoctorCardSkeleton = () => (
  <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
    <div className="flex items-start gap-6">
      {/* Profile Image Skeleton */}
      <div className="w-24 h-24 bg-gray-200 rounded-full animate-pulse flex-shrink-0"></div>
      
      <div className="flex-1 min-w-0">
        {/* Name Skeleton */}
        <div className="h-6 bg-gray-200 rounded w-32 mb-2 animate-pulse"></div>
        
        {/* Specialization Skeleton */}
        <div className="h-4 bg-gray-200 rounded w-24 mb-3 animate-pulse"></div>

        {/* Details Skeleton */}
        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
            <div className="h-4 bg-gray-200 rounded w-24 animate-pulse"></div>
          </div>
          <div className="flex gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-20 animate-pulse"></div>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              <div className="h-4 bg-gray-200 rounded w-16 animate-pulse"></div>
            </div>
          </div>
        </div>

        {/* Availability Skeleton */}
        <div className="h-4 bg-gray-200 rounded w-32 mb-3 animate-pulse"></div>

        {/* Description Skeleton */}
        <div className="space-y-2 mb-6">
          <div className="h-4 bg-gray-200 rounded w-full animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded w-3/4 animate-pulse"></div>
        </div>

        {/* Button Skeleton */}
        <div className="h-5 bg-gray-200 rounded w-20 animate-pulse"></div>
      </div>
    </div>
  </div>
);

const NurseList = () => {
  const navigate = useNavigate();

  // State for API data
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch doctors data from API
  useEffect(() => {
    const fetchDoctors = async () => {
      try {
        setLoading(true);
        const response = await userAPI.filterUsers({
          role: 'nurse',
          page: currentPage,
          limit: 10
        });
        const nursesData = response.users || [];

        // Transform API data to match the expected format
        const transformedNurses = nursesData.map((nurse, index) => {
          return {
            isCertified:nurse.isCertified,
            id: nurse._id,
            name: nurse.name,
            specialization: `${
              nurse.role === "nurse" ? "Nurse" : "Healthcare Professional"
            }`,
            languages: "English",
            gender: "Not specified",
            experience: "Experience not specified",
            availability: "Available for booking",
            description: `${nurse.name} is an experienced ${nurse.role} with a focus on patient care and treatment. Specialized in healthcare and patient support.`,
            image: nurse.profileImage 
              ? (nurse.profileImage.startsWith('https') 
                  ? nurse.profileImage 
                : `https://backend.adhdrapidcare.com${nurse.profileImage}`)
              : "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=100&q=80",
          };
        });
        setDoctors((transformedNurses ?? []).filter(i => i.isCertified === true));
        setTotalPages(response.pagination?.totalPages || 1);
      } catch (err) {
        console.error("Error fetching nurses:", err);
        setError("Failed to load nurses");
      } finally {
        setLoading(false);
      }
    };

    fetchDoctors();
  }, [currentPage]);

  const handleViewProfile = (nurseId) => {
    navigate(`/nurse-profile/${nurseId}`);
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Main Content */}
      <main className="flex-grow py-12 container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-left mb-8">
          <div className="flex items-start gap-3 mb-4">
            <button
              onClick={handleGoBack}
              className="flex items-center justify-center w-10 h-10 rounded-full bg-white border border-gray-200 hover:bg-gray-50 transition-colors mt-1"
            >
              <svg
                className="w-5 h-5 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div>
              <h1 className="text-xl md:text-2xl font-semibold text-gray-800 mb-1">
                Select a Nurse
              </h1>
              <p className="text-gray-600">
                Connect with ADHD trained healthcare professionals
              </p>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {error && (
          <div className="flex items-center justify-center h-64 mb-8">
            <div className="text-center">
              <p className="text-red-600 mb-4">{error}</p>
              <button
                onClick={handleGoBack}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition"
              >
                Go Back
              </button>
            </div>
          </div>
        )}

        {/* Doctor Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {loading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <DoctorCardSkeleton key={index} />
            ))
          ) : 
          doctors.length<1?(
            <div className="col-span-full flex flex-col items-center justify-center   rounded-xl p-8 text-center">
            <h3 className="text-lg font-semibold text-gray-700">No Nurse available</h3>
            <p className="text-gray-500 mt-1 mb-4">
              Once doctors are added, you’ll see them listed here.
            </p>
          </div>
          ):
          (
            doctors.map((doc, index) => (
              <div
                key={doc.id}
                onClick={() => handleViewProfile(doc.id)}
                className="bg-white rounded-xl shadow-md border border-gray-200 p-6 cursor-pointer hover:shadow-lg transition-shadow text-center"
              >
                {/* Image */}
                <img
                  src={doc.image}
                  alt={doc.name}
                  className="w-24 h-24 mx-auto rounded-full  mb-4"
                />

                {/* Name */}
                <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">
                  {doc.name}
                </h3>

                {/* Blue Chip */}
                <div className="inline-block px-3 py-1 text-sm font-medium bg-blue-100 text-blue-600 rounded-full">
                  ADHD Qualified
                </div>
              </div>
            ))
          )}
        </div>

        

        {/* Pagination - Only show when not loading and no error */}
        {!loading && !error && totalPages > 0 && (
          <div className="flex justify-center items-center gap-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                ></path>
              </svg>
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`w-8 h-8 rounded-full text-sm font-medium transition-colors ${
                    currentPage === pageNumber
                      ? "bg-blue-500 text-white"
                      : "bg-white border border-gray-300 hover:bg-gray-100 text-gray-700"
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-200 text-gray-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 5l7 7-7 7"
                ></path>
              </svg>
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default NurseList;